import type { ReactNode } from 'react';

import type { AdminRunTelemetry } from '@/api/adminClient';
import { formatUsd } from '@/lib/format';
import {
  MARKER_LABELS,
  engineSummary,
  formatBytes,
  formatDurationMs,
  formatTokens,
  markerOffset,
  ompRecoverySummary,
} from '@/lib/runTelemetry';
import { cn } from '@/lib/utils';

function MetricTile({
  label,
  value,
  sub,
  emphasis,
}: {
  label: string;
  value: string | undefined;
  sub?: ReactNode;
  emphasis?: boolean;
}) {
  const missing = value === undefined || value === '—';
  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-background/40 p-4',
        emphasis && !missing && 'border-warning/40',
      )}
    >
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p
        className={cn(
          'mt-1.5 font-semibold tabular-nums',
          (value?.length ?? 0) > 12 ? 'text-base' : 'text-lg',
          missing && 'text-sm font-normal italic text-muted-foreground',
        )}
      >
        {missing ? 'Not reported' : value}
      </p>
      {sub ? <div className="mt-1 text-xs leading-5 text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold text-muted-foreground">{title}</h3>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{children}</div>
    </div>
  );
}

function joinParts(parts: Array<string | undefined | false>): string | undefined {
  const kept = parts.filter((part): part is string => Boolean(part));
  return kept.length > 0 ? kept.join(' · ') : undefined;
}

export function RunTelemetryPanel({ telemetry }: { telemetry: AdminRunTelemetry }) {
  const { ttft, firstTool, seat, startContext, resumeContext, usage, exploration, writeGate, model, effort } =
    telemetry;
  const buildToFirstTool = markerOffset(telemetry, 'agent.first_tool');
  const cacheLookups =
    startContext?.cacheHits !== undefined && startContext.cacheMisses !== undefined
      ? startContext.cacheHits + startContext.cacheMisses
      : undefined;
  const resolvedModel = model.resolved.length > 0 ? model.resolved.join(', ') : undefined;

  return (
    <div className="space-y-6">
      <Group title="Latency">
        <MetricTile
          label="Time to first model output"
          value={ttft.ms !== undefined ? formatDurationMs(ttft.ms) : undefined}
          emphasis={(ttft.ms ?? 0) >= 20_000}
          sub={
            ttft.source === 'reported'
              ? joinParts([
                  'Reported by executor (ttftMs)',
                  ttft.promptBytes !== undefined && `request ${formatBytes(ttft.promptBytes)}`,
                ])
              : ttft.source === 'stream'
                ? `First streamed OpenCode part, from “${MARKER_LABELS[ttft.baseline ?? 'agent.editing.start']}”`
                : 'No ttftMs and no streamed model output for this run'
          }
        />
        <MetricTile
          label="Started writing a file"
          value={firstTool.firstWriteStartedMs !== undefined ? formatDurationMs(firstTool.firstWriteStartedMs) : undefined}
          sub={
            firstTool.firstWriteStartedMs !== undefined
              ? joinParts([firstTool.firstWritePath, 'when “Writing <file>” could show, from agent spawn'])
              : 'Not recorded for this run'
          }
        />
        <MetricTile
          label="First tool finished"
          value={firstTool.ms !== undefined ? formatDurationMs(firstTool.ms) : undefined}
          emphasis={(firstTool.ms ?? 0) >= 30_000}
          sub={joinParts([
            firstTool.tool && `first call: ${firstTool.tool}`,
            'firstToolMs, measured from agent spawn',
          ])}
        />
        <MetricTile
          label="First file saved"
          value={firstTool.firstEditMs !== undefined ? formatDurationMs(firstTool.firstEditMs) : undefined}
          sub="firstEditMs, measured from agent spawn"
        />
        <MetricTile
          label="Build → first tool"
          value={buildToFirstTool !== undefined ? formatDurationMs(buildToFirstTool) : undefined}
          sub={telemetry.anchor ? `From “${MARKER_LABELS[telemetry.anchor.key]}”` : 'No Build/prompt event for this run'}
        />
        <MetricTile
          label="Seat claim"
          value={seat.claimMs !== undefined ? formatDurationMs(seat.claimMs) : undefined}
          emphasis={(seat.claimMs ?? 0) >= 20_000}
          sub={joinParts([
            seat.seat,
            seat.prewarmed === true && 'prewarmed',
            seat.prewarmed === false && 'not prewarmed',
            seat.workspaceReused === true && 'workspace reused',
            seat.viteReadyMs !== undefined && `Vite ${formatDurationMs(seat.viteReadyMs)}`,
            seat.queuedMs !== undefined && `queued ${formatDurationMs(seat.queuedMs)}`,
          ])}
        />
      </Group>

      <Group title="Model">
        <MetricTile
          label="Model"
          value={resolvedModel ?? model.requestModel ?? model.agent ?? model.requested}
          sub={joinParts([
            model.requested && `requested ${model.requested}`,
            model.agent && model.agent !== model.requested && `agent ${model.agent}`,
            model.requestModel && `request ${model.requestModel}`,
            engineSummary(model),
            ompRecoverySummary(telemetry.omp),
            !resolvedModel && 'resolved model not reported yet',
          ])}
        />
        <MetricTile
          label="Effort requested"
          value={effort.requested}
          sub={effort.requested ? 'executorEffort on the run' : 'No effort set on the run'}
        />
        <MetricTile
          label="Effort effective"
          value={effort.effective}
          emphasis={Boolean(effort.effective && effort.requested && effort.effective !== effort.requested)}
          sub={
            effort.effective
              ? effort.requested && effort.effective !== effort.requested
                ? `Differs from requested (${effort.requested})`
                : 'Reported on the LLM request'
              : 'Only known once the executor reports it on agent.llm.request_sent'
          }
        />
        <MetricTile
          label="Turns"
          value={usage.turns !== undefined ? formatTokens(usage.turns) : undefined}
          sub={usage.durationMs !== undefined ? `Agent time ${formatDurationMs(usage.durationMs)}` : undefined}
        />
      </Group>

      <Group title="Context & cache">
        <MetricTile
          label="Start context"
          value={startContext?.bytes !== undefined ? formatBytes(startContext.bytes) : undefined}
          sub={joinParts([
            startContext?.files !== undefined && `${startContext.files} files`,
            startContext?.seededFiles !== undefined && `${startContext.seededFiles} seeded`,
            startContext?.outcome,
            startContext?.durationMs !== undefined && `built in ${formatDurationMs(startContext.durationMs)}`,
            resumeContext?.mode && `resume ${resumeContext.mode}`,
          ])}
        />
        <MetricTile
          label="Seat file cache"
          value={
            cacheLookups !== undefined
              ? cacheLookups === 0
                ? 'No lookups'
                : `${Math.round(((startContext?.cacheHits ?? 0) / cacheLookups) * 100)}% hit`
              : undefined
          }
          sub={
            cacheLookups !== undefined
              ? `${startContext?.cacheHits} hits · ${startContext?.cacheMisses} misses (toolReadCache)`
              : 'Seat read-cache counts from agent.start_context'
          }
        />
        <MetricTile
          label="LLM cache reads"
          value={usage.cacheReadTokens !== undefined ? `${formatTokens(usage.cacheReadTokens)} tok` : undefined}
          sub="Run total across all turns — does not show whether turn 1 was cached"
        />
        <MetricTile
          label="Tokens in / out"
          value={
            usage.inputTokens !== undefined || usage.outputTokens !== undefined
              ? `${formatTokens(usage.inputTokens)} / ${formatTokens(usage.outputTokens)}`
              : undefined
          }
          sub={joinParts([
            usage.costUsd !== undefined && formatUsd(usage.costUsd),
            usage.metered === false && 'unmetered',
          ])}
        />
      </Group>

      <Group title="Exploration">
        <MetricTile
          label="Reads before first edit"
          value={exploration?.readsBeforeEdit !== undefined ? String(exploration.readsBeforeEdit) : undefined}
          emphasis={(exploration?.readsBeforeEdit ?? 0) >= 6}
          sub={joinParts([
            exploration?.totalReadTools !== undefined && `${exploration.totalReadTools} reads`,
            exploration?.totalWriteTools !== undefined && `${exploration.totalWriteTools} writes`,
            exploration?.maxConsecutiveReads !== undefined && `max ${exploration.maxConsecutiveReads} in a row`,
          ])}
        />
        <MetricTile
          label="Explore loop"
          value={
            exploration?.exploreLoopDetected === undefined
              ? undefined
              : exploration.exploreLoopDetected
                ? 'Detected'
                : 'None'
          }
          emphasis={exploration?.exploreLoopDetected === true}
          sub="From agent.edit_metrics"
        />
        <MetricTile
          label="Write-first gate"
          value={
            writeGate
              ? writeGate.ok === undefined
                ? writeGate.enforced
                  ? 'Armed'
                  : 'Off'
                : writeGate.ok
                  ? 'Passed'
                  : 'Failed'
              : undefined
          }
          emphasis={writeGate?.ok === false}
          sub={joinParts([
            writeGate?.mode,
            writeGate?.firstTool && `first tool ${writeGate.firstTool}`,
            writeGate?.reason,
          ])}
        />
        <MetricTile
          label="Heartbeats during edit"
          value={
            telemetry.markers.some((marker) => marker.key === 'agent.editing.start')
              ? String(telemetry.heartbeatCount)
              : undefined
          }
          sub="agent.editing.heartbeat every ~15 s while the agent runs"
        />
      </Group>
    </div>
  );
}
